import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import useScreenshotProtection from "@/hooks/useScreenshotProtection";

import Login from "@/pages/Login";
import ShopkeeperHome from "@/pages/shopkeeper/Home";
import PlaceOrder from "@/pages/shopkeeper/PlaceOrder";
import MyOrders from "@/pages/shopkeeper/MyOrders";
import Profile from "@/pages/shopkeeper/Profile";
import ContactUs from "@/pages/shopkeeper/ContactUs";
import SalesmanHome from "@/pages/salesman/Home";
import SalesmanMap from "@/pages/salesman/Map";
import OrderDetail from "@/pages/salesman/OrderDetail";
import DirectSale from "@/pages/salesman/DirectSale";
import SalesHistory from "@/pages/salesman/SalesHistory";
import ManagerDashboard from "@/pages/manager/Dashboard";
import ManagerOrdersList from "@/pages/manager/OrdersList";
import ManagerPerformance from "@/pages/manager/Performance";
import ManagerStockLoad from "@/pages/manager/StockLoad";
import ManagerStockReturn from "@/pages/manager/StockReturn";
import ManagerDepotReceived from "@/pages/manager/DepotReceived";
import ManagerDepotBalance from "@/pages/manager/DepotBalance";
import SalesmanMyStock from "@/pages/salesman/MyStock";
import ManagementDashboard from "@/pages/management/Dashboard";
import ManagementDrilldown from "@/pages/management/Drilldown";
import ManagementStockReport from "@/pages/management/StockReport";
import SalesmanDrilldown from "@/pages/salesman/Drilldown";
import Attendance from "@/pages/Attendance";
import ManagementAttendance from "@/pages/management/AttendancePage";
import ManagementLiveTracking from "@/pages/management/LiveTracking";
import ManagementDepotStock from "@/pages/management/DepotStockPage";
import ManagementRouteHistory from "@/pages/management/RouteHistory";
import ManagementAttendanceReport from "@/pages/management/AttendanceReport";
import api from "@/api";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminSales from "@/pages/admin/Sales";
import AdminStock from "@/pages/admin/Stock";
import AdminInfo from "@/pages/admin/Info";
import AdminPinControl from "@/pages/admin/PinControl";
import OpsAdminDashboard from "@/pages/ops/Dashboard";
import AssignStores from "@/pages/ops/AssignStores";
import AddShop from "@/pages/field/AddShop";

const RoleHome = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  switch (user.role) {
    case "shopkeeper": return <Navigate to="/shop" replace />;  // legacy
    case "retailer": return <Navigate to="/shop" replace />;
    case "wholesaler": return <Navigate to="/shop" replace />;
    case "salesman": return <Navigate to="/salesman" replace />;
    case "manager": return <Navigate to="/manager" replace />;
    case "management": return <Navigate to="/management" replace />;
    case "ops_admin": return <Navigate to="/ops" replace />;
    case "super_admin": return <Navigate to="/admin" replace />;
    default: return <Navigate to="/login" replace />;
  }
};

const Protected = ({ roles, children }) => {
  // Tier 1 (Feb 2026): Attendance gate removed. Auto check-in now happens silently in
  // AuthContext.login(); manual /attendance page still accessible for legacy/manual flows.
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#86868B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

// Tier 3 (Feb 2026): Inner component runs inside AuthProvider so the
// screenshot-protection hook can read user.role and toggle FLAG_SECURE
// on Capacitor APK builds for salesman/manager.
function AppRoutes() {
  useScreenshotProtection();
  return (
    <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/attendance" element={<Protected roles={["salesman", "manager"]}><Attendance /></Protected>} />
            <Route path="/" element={<RoleHome />} />
            <Route path="/shop" element={<Protected roles={["shopkeeper", "retailer", "wholesaler"]}><ShopkeeperHome /></Protected>} />
            <Route path="/shop/place" element={<Protected roles={["shopkeeper", "retailer", "wholesaler"]}><PlaceOrder /></Protected>} />
            <Route path="/shop/orders" element={<Protected roles={["shopkeeper", "retailer", "wholesaler"]}><MyOrders /></Protected>} />
            <Route path="/shop/profile" element={<Protected roles={["shopkeeper", "retailer", "wholesaler", "salesman", "manager", "management", "super_admin"]}><Profile /></Protected>} />
            <Route path="/shop/contact" element={<Protected roles={["shopkeeper", "retailer", "wholesaler"]}><ContactUs /></Protected>} />
            <Route path="/salesman" element={<Protected roles={["salesman"]}><SalesmanHome /></Protected>} />
            <Route path="/salesman/map" element={<Protected roles={["salesman"]}><SalesmanMap /></Protected>} />
            <Route path="/salesman/direct-sale" element={<Protected roles={["salesman"]}><DirectSale /></Protected>} />
            <Route path="/salesman/sales-history" element={<Protected roles={["salesman"]}><SalesHistory /></Protected>} />
            <Route path="/salesman/stock" element={<Protected roles={["salesman"]}><SalesmanMyStock /></Protected>} />
            <Route path="/salesman/drilldown/:variant" element={<Protected roles={["salesman"]}><SalesmanDrilldown /></Protected>} />
            <Route path="/salesman/order/:id" element={<Protected roles={["salesman"]}><OrderDetail /></Protected>} />
            <Route path="/manager" element={<Protected roles={["manager"]}><ManagerDashboard /></Protected>} />
            <Route path="/manager/orders" element={<Protected roles={["manager"]}><ManagerOrdersList /></Protected>} />
            <Route path="/manager/orders/:variant" element={<Protected roles={["manager"]}><ManagerOrdersList /></Protected>} />
            <Route path="/manager/performance" element={<Protected roles={["manager"]}><ManagerPerformance /></Protected>} />
            <Route path="/manager/direct-sale" element={<Protected roles={["manager"]}><DirectSale /></Protected>} />
            <Route path="/manager/sales-history" element={<Protected roles={["manager"]}><SalesHistory /></Protected>} />
            <Route path="/manager/stock-load" element={<Protected roles={["manager"]}><ManagerStockLoad /></Protected>} />
            <Route path="/manager/stock-return" element={<Protected roles={["manager"]}><ManagerStockReturn /></Protected>} />
            <Route path="/manager/depot-received" element={<Protected roles={["manager", "management", "super_admin"]}><ManagerDepotReceived /></Protected>} />
            <Route path="/manager/depot-balance" element={<Protected roles={["manager", "management", "super_admin"]}><ManagerDepotBalance /></Protected>} />
            <Route path="/management" element={<Protected roles={["management", "super_admin"]}><ManagementDashboard /></Protected>} />
            <Route path="/management/stock-report" element={<Protected roles={["management", "super_admin"]}><ManagementStockReport /></Protected>} />
            <Route path="/management/attendance" element={<Protected roles={["management", "super_admin"]}><ManagementAttendance /></Protected>} />
            <Route path="/management/live-tracking" element={<Protected roles={["management", "super_admin", "ops_admin"]}><ManagementLiveTracking /></Protected>} />
            <Route path="/management/route-history" element={<Protected roles={["management", "super_admin", "manager"]}><ManagementRouteHistory /></Protected>} />
            <Route path="/management/attendance-report" element={<Protected roles={["management", "super_admin"]}><ManagementAttendanceReport /></Protected>} />
            <Route path="/management/depot-stock" element={<Protected roles={["management", "super_admin"]}><ManagementDepotStock /></Protected>} />
            <Route path="/management/:variant" element={<Protected roles={["management", "super_admin"]}><ManagementDrilldown /></Protected>} />
            <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/sales" element={<Protected roles={["super_admin", "management", "ops_admin"]}><AdminSales /></Protected>} />
            <Route path="/admin/stock" element={<Protected roles={["super_admin", "management", "ops_admin"]}><AdminStock /></Protected>} />
            <Route path="/admin/info" element={<Protected roles={["super_admin", "management"]}><AdminInfo /></Protected>} />
            <Route path="/admin/pin-control" element={<Protected roles={["super_admin"]}><AdminPinControl /></Protected>} />
            <Route path="/ops" element={<Protected roles={["ops_admin", "super_admin"]}><OpsAdminDashboard /></Protected>} />
            <Route path="/ops/assign" element={<Protected roles={["ops_admin", "super_admin", "manager"]}><AssignStores /></Protected>} />
            <Route path="/field/add-shop" element={<Protected roles={["salesman", "manager", "ops_admin", "super_admin"]}><AddShop /></Protected>} />
          </Routes>
  );
}

export default App;
